import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ProjectFilters } from '../services/api';
import { parsePeSearchState, writePeFiltersToSearch } from '../utils/searchState';

function filtersFromSearch(search: string, dropProjectIds: boolean): ProjectFilters {
  const next = { ...parsePeSearchState(search).filters };
  if (dropProjectIds) delete next.projectIds;
  return next;
}

function omitProjectIds(filters: ProjectFilters): ProjectFilters {
  if (!filters.projectIds?.length) return filters;
  const next = { ...filters };
  delete next.projectIds;
  return next;
}

function peFiltersEqual(a: ProjectFilters, b: ProjectFilters): boolean {
  const key = (f: ProjectFilters) =>
    [
      (f.regions ?? []).slice().sort().join(','),
      (f.districts ?? []).slice().sort().join(','),
      (f.propertyTypes ?? []).slice().sort().join(','),
      (f.projectIds ?? []).slice().sort().join(','),
      f.soleMarketing ? '1' : '0',
    ].join('|');
  return key(a) === key(b);
}

/**
 * Catalogue-page filters that stay in the URL so the top nav can carry
 * them to the next Project Essentials page (e.g. Resi Mixed → International).
 */
export function usePeFilters(opts?: {
  dropProjectIds?: boolean;
}): [ProjectFilters, Dispatch<SetStateAction<ProjectFilters>>] {
  const location = useLocation();
  const navigate = useNavigate();
  const dropProjectIds = opts?.dropProjectIds === true;

  const [filters, setFiltersState] = useState<ProjectFilters>(() =>
    filtersFromSearch(location.search, dropProjectIds),
  );

  useEffect(() => {
    const next = filtersFromSearch(location.search, dropProjectIds);
    setFiltersState((prev) => (peFiltersEqual(prev, next) ? prev : next));
  }, [location.search, dropProjectIds]);

  const setFilters = useCallback(
    (next: SetStateAction<ProjectFilters>) => {
      setFiltersState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        const toWrite = dropProjectIds ? omitProjectIds(resolved) : resolved;
        const search = writePeFiltersToSearch(location.search, toWrite);
        if (search !== (location.search || '')) {
          navigate({ pathname: location.pathname, search }, { replace: true });
        }
        return peFiltersEqual(prev, toWrite) ? prev : toWrite;
      });
    },
    [dropProjectIds, location.pathname, location.search, navigate],
  );

  return [filters, setFilters];
}
